import React, {useContext, useEffect, useMemo, useState} from "react";
import PageBuilder from "../../../components/panel/PageBuilder";
import {ModalConfirmContext} from "../../../providers/ModalConfirmProvider";
import {useNavigate, useParams} from "react-router-dom";
import {
    useCloneCompanyProductMutation,
    useDeleteCompanyProductMutation,
    useGetCompanyProductMutation
} from "../../../api/companyCatalogApi";
import {CatalogPreviewItemType} from "../../../api/shareApi";
import {Button, Card, Slider, Space, Spin} from "antd";
import {CopyOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import styled from "styled-components";
import ProductAttributesSelect from "../../../components/ProductAttributesSelect/ProductAttributesSelect";
import ProductPrice from "../../../components/ProductPrice/ProductPrice";
import {useGetAssetByIdQuery} from "../../../api/assetApi";
import {Asset3DType} from "../components/share/ShareProductCode";
import {AMAMode, EditorConstructor} from "@amaspace-editor/editor-3d";
import toast from "react-hot-toast";

const CompanyProductView = () => {
    const {showModal} = useContext(ModalConfirmContext)
    const {id} = useParams()
    const navigate = useNavigate()

    const [product, setProduct] = useState<CatalogPreviewItemType | null>(null)
    const [allProducts, setAllProducts] = useState<CatalogPreviewItemType[]>([])
    const [modelSizeInAR, setModelSizeInAR] = useState(1)

    const [getProduct, resultsProduct] = useGetCompanyProductMutation()
    const [deleteItem] = useDeleteCompanyProductMutation()
    const [cloneItem] = useCloneCompanyProductMutation()

    useEffect(() => {
        getProduct({id: id!})
    }, []);

    useEffect(() => {
        if (resultsProduct.isSuccess) {
            const {data} = resultsProduct
            const product = data as unknown as CatalogPreviewItemType
            setProduct({
                ...product,
                price: Array.isArray(product?.price) ? undefined : product?.price
            })
        }
    }, [resultsProduct.isSuccess])

    const productName = product?.name ?? ''

    if (resultsProduct.isLoading) {
        return <Spin size="large"/>
    }

    return (
        <PageBuilder title={`View: ${productName}`} documentTitle={`View: ${productName}`}>
            {
                product && (<>
                    <Space style={{marginBottom: 20}}>
                        <Button
                            onClick={() => {
                                navigate(`/company/catalog/edit/${id}`)
                            }}
                            type="primary"
                        >
                            <EditOutlined/> Edit Product
                        </Button>

                        <Button
                            onClick={() => {
                                const handleDelete = async () => {
                                    const loader = toast.loading("Progressing...");
                                    const res = await deleteItem([id!])
                                    toast.dismiss(loader);
                                    if ('error' in res) return
                                    toast.success('Product is successfully deleted!')
                                    navigate("/company/catalog")
                                }

                                showModal('delete product', handleDelete)
                            }}
                            type="primary"
                        >
                            <DeleteOutlined/> Delete Product
                        </Button>

                        <Button
                            onClick={() => {
                                const handleDelete = async () => {
                                    const loader = toast.loading("Progressing...");
                                    const res = await cloneItem([id!])
                                    toast.dismiss(loader);
                                    if ('error' in res) return
                                    toast.success('Product is successfully duplicated!')
                                    navigate("/company/catalog")
                                }

                                showModal('duplicate product', handleDelete)
                            }}
                            type="primary"
                        >
                            <CopyOutlined/> Duplicate Product
                        </Button>
                    </Space>

                    <div style={{marginTop: '16px'}}>
                        <Card style={{marginBottom: 8}} title="Preview">
                            <StyledViewerWrapper block>
                                <StyledEditorViewer>
                                    {/* {isMobile && (
                                        <StyledARControlsWrapper>
                                            <StyledARButton
                                                onClick={() => {
                                                    editor3D.startARSession()
                                                }}>
                                                <IconAR />
                                            </StyledARButton>
                                            <StyledWrapperControlsARModelSize>
                                                Model scale x in AR mode
                                                <StyledControlsARModelSize>
                                                    <StyledInputRangeARModelSize
                                                        min={0.01}
                                                        max={5}
                                                        onChange={(value: number) => {
                                                            setModelSizeInAR(value)
                                                            editor3D.androidARController?.setModelSize(value)
                                                        }}
                                                        step={0.01}
                                                        value={modelSizeInAR}
                                                    />
                                                    <InputNumber
                                                        min={0.01}
                                                        max={20}
                                                        step={0.01}
                                                        value={modelSizeInAR}
                                                        onChange={(value: number | null) => {
                                                            if (!value) {
                                                                setModelSizeInAR(0.01)
                                                                editor3D.androidARController?.setModelSize(0.01)
                                                                return
                                                            }
                                                            if (typeof value === 'number') {
                                                                setModelSizeInAR(value)
                                                                editor3D.androidARController?.setModelSize(value)
                                                                return
                                                            }
                                                        }}
                                                    />
                                                </StyledControlsARModelSize>
                                            </StyledWrapperControlsARModelSize>
                                        </StyledARControlsWrapper>
                                    )} */}
                                    <React.Suspense fallback={null}>
                                        <Render3D
                                            id={product.asset_customizer_id}
                                            type={product.asset_type as Asset3DType}
                                        />
                                    </React.Suspense>
                                </StyledEditorViewer>
                                <StyledEditorAttributes>
                                    {product?.attributes?.length ? (
                                        <ProductAttributesSelect
                                            // accessToken={authToken?.accessToken}
                                            allProducts={allProducts}
                                            product={product}
                                            setAllProducts={setAllProducts}
                                        />
                                    ) : null}
                                    {product?.price ? <ProductPrice product={product} products={allProducts}/> : null}
                                </StyledEditorAttributes>
                            </StyledViewerWrapper>
                        </Card>
                    </div>
                </>)
            }
        </PageBuilder>
    );
};

export default CompanyProductView;

const Render3D = React.memo(({id, type}: {
    type: Asset3DType
    id: string | null
}): JSX.Element => {
    const fetchAssetByIdResponse = useGetAssetByIdQuery({id: id as string})

    const data = useMemo(() => {
        if (!fetchAssetByIdResponse.isSuccess) return
        return structuredClone(fetchAssetByIdResponse.data)
    }, [fetchAssetByIdResponse])

    if (fetchAssetByIdResponse.isSuccess) {
        return <EditorConstructor type={type} data={data} presetConfig={{mode: AMAMode.VIEWER}}/>
    }
    return <></>
})

const StyledViewerWrapper = styled(Space.Compact)`
    display: flex;
    gap: 10px;
    height: calc(100vh - 500px);
`
const StyledEditorViewer = styled.div`
    position: relative;
    flex: 1;
`
const StyledEditorAttributes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 0.3;

    height: 100%;
    overflow-y: auto;
`

const StyledARControlsWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: 24px;
    left: 12px;
    position: absolute;
    top: 12px;
    z-index: 5;
`

const StyledARButton = styled.button`
    align-items: center;
    background-color: rgb(255, 255, 255);
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    height: 48px;
    justify-content: center;
    margin: 0px;
    padding: 10px;
    width: 48px;
`

const StyledWrapperControlsARModelSize = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const StyledControlsARModelSize = styled.div`
    align-items: center;
    display: flex;
    gap: 6px;
`

const StyledInputRangeARModelSize = styled(Slider)`
    flex-basis: 160px;
    margin: 0;
`

